/**
 * NEXMIO — helpers for the nx- sections. Dependency free.
 *
 * - Videos use preload="none" and only download when they are about to play.
 * - `[data-nx-autoplay]` videos play muted while visible and pause when scrolled away.
 * - Reels: only one reel plays at a time.
 * - `.nx-rail` prev / next buttons (desktop).
 * - Sticky add-to-cart bar on product pages (mobile).
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hasIO = 'IntersectionObserver' in window;

  const wrapperOf = (video) => video.closest('[data-nx-video]') || video.parentElement;

  function play(video) {
    video.muted = true;
    const attempt = video.play();
    if (attempt && attempt.catch) {
      attempt.catch(() => {
        // Autoplay blocked (e.g. Low Power Mode): hand over to native controls.
        video.controls = true;
        wrapperOf(video)?.setAttribute('data-nx-native', '');
      });
    }
  }

  const viewportObserver = hasIO
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = entry.target;
            if (entry.isIntersecting) {
              if (video.dataset.nxUserPaused !== 'true') play(video);
            } else if (!video.paused) {
              video.pause();
            }
          }
        },
        { threshold: 0.35 }
      )
    : null;

  function initVideos(root) {
    root.querySelectorAll('video[data-nx-video-el]:not([data-nx-ready])').forEach((video) => {
      video.setAttribute('data-nx-ready', '');
      const wrapper = wrapperOf(video);
      const inReel = Boolean(video.closest('[data-nx-reels]'));

      video.addEventListener('playing', () => wrapper?.setAttribute('data-nx-playing', ''));
      video.addEventListener('pause', () => wrapper?.removeAttribute('data-nx-playing'));
      video.addEventListener('ended', () => wrapper?.removeAttribute('data-nx-playing'));

      wrapper?.querySelector('[data-nx-play]')?.addEventListener('click', () => {
        if (video.paused) {
          video.dataset.nxUserPaused = 'false';
          if (inReel) pauseSiblings(video);
          play(video);
        } else {
          video.dataset.nxUserPaused = 'true';
          video.pause();
        }
      });

      if (inReel || !viewportObserver) return;
      if (!video.hasAttribute('data-nx-autoplay') || reducedMotion.matches) {
        video.dataset.nxUserPaused = 'true';
      }
      viewportObserver.observe(video);
    });
  }

  function pauseSiblings(current) {
    current
      .closest('[data-nx-reels]')
      ?.querySelectorAll('video')
      .forEach((v) => v !== current && !v.paused && v.pause());
  }

  function initReels(root) {
    root.querySelectorAll('[data-nx-reels]:not([data-nx-ready])').forEach((reels) => {
      reels.setAttribute('data-nx-ready', '');
      const rail = reels.querySelector('.nx-rail');
      if (!rail || !hasIO) return;

      const autoplay = reels.getAttribute('data-nx-reels') === 'autoplay' && !reducedMotion.matches;
      const visible = new Set();
      let inView = false;

      const update = () => {
        const videos = [...reels.querySelectorAll('video')];
        if (!inView) {
          videos.forEach((v) => !v.paused && v.pause());
          return;
        }
        if (!autoplay) {
          videos.forEach((v) => !visible.has(v) && !v.paused && v.pause());
          return;
        }
        const userPlaying = videos.find((v) => !v.paused && v.dataset.nxUserPaused === 'false');
        if (userPlaying && visible.has(userPlaying)) return;

        const left = rail.getBoundingClientRect().left;
        let best = null;
        let bestOffset = Infinity;
        for (const v of videos) {
          if (!visible.has(v) || v.dataset.nxUserPaused === 'true') continue;
          const offset = Math.abs(v.getBoundingClientRect().left - left);
          if (offset < bestOffset) {
            best = v;
            bestOffset = offset;
          }
        }
        videos.forEach((v) => {
          if (v === best) {
            if (v.paused) play(v);
          } else if (!v.paused) {
            v.pause();
          }
        });
      };

      new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          update();
        },
        { threshold: 0.4 }
      ).observe(rail);

      const reelObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
          update();
        },
        { root: rail, threshold: 0.9 }
      );
      reels.querySelectorAll('video').forEach((v) => reelObserver.observe(v));
    });
  }

  function initRails(root) {
    root.querySelectorAll('[data-nx-rail-nav]:not([data-nx-ready])').forEach((nav) => {
      nav.setAttribute('data-nx-ready', '');
      const rail = nav.closest('.nx-section')?.querySelector('.nx-rail');
      const [prev, next] = nav.querySelectorAll('button');
      if (!rail || !prev || !next) return;

      const sync = () => {
        const max = rail.scrollWidth - rail.clientWidth - 2;
        nav.hidden = max <= 0;
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= max;
      };
      const move = (dir) =>
        rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: reducedMotion.matches ? 'auto' : 'smooth' });

      prev.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      rail.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /**
   * Sticky add-to-cart (mobile). It never submits on its own: it clicks Dawn's real
   * product-form button so AJAX add, cart drawer and error handling stay untouched.
   */
  function initStickyAtc(root) {
    root.querySelectorAll('[data-nx-sticky-atc]:not([data-nx-ready])').forEach((bar) => {
      bar.setAttribute('data-nx-ready', '');
      const sectionId = bar.dataset.sectionId;
      const form = document.querySelector(`product-form form[id^="product-form-${sectionId}"]`)
        || document.querySelector(`#MainProduct-${sectionId} product-form form`);
      const submit = form?.querySelector('[type="submit"]');
      const button = bar.querySelector('button');
      const priceEl = bar.querySelector('[data-nx-sticky-price]');
      if (!submit || !button) return;

      button.addEventListener('click', () => {
        if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') {
          submit.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
          return;
        }
        submit.click();
      });

      const syncState = () => {
        const disabled = submit.disabled || submit.getAttribute('aria-disabled') === 'true';
        const label = submit.querySelector('span')?.textContent?.trim();
        if (label) button.textContent = label;
        button.classList.toggle('button--secondary', disabled);
        const pr = document.getElementById(`price-${sectionId}`);
        if (priceEl && pr && pr.dataset.nx2Total) {
          // Bundle row selected (nx2-bundle-offer): show the discounted total with the original struck through.
          const want = `${pr.dataset.nx2Total}|${pr.dataset.nx2Orig}`;
          if (priceEl.dataset.nx2 !== want) {
            priceEl.textContent = `${pr.dataset.nx2Total} `;
            const s = document.createElement('s');
            s.style.opacity = '.6';
            s.style.fontWeight = '400';
            s.textContent = pr.dataset.nx2Orig;
            priceEl.appendChild(s);
            priceEl.dataset.nx2 = want;
          }
          return;
        }
        if (priceEl) delete priceEl.dataset.nx2;
        const price = document.querySelector(`#price-${sectionId} .price__regular .price-item, #price-${sectionId} .price__sale .price-item--sale`);
        if (price && priceEl) priceEl.textContent = price.textContent.trim();
      };
      syncState();
      new MutationObserver(syncState).observe(submit, { attributes: true, childList: true, subtree: true });
      const priceRoot = document.querySelector(`#price-${sectionId}`);
      if (priceRoot) new MutationObserver(syncState).observe(priceRoot, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['data-nx2-total', 'data-nx2-orig'] });

      if (!hasIO) return;
      new IntersectionObserver(
        ([entry]) => {
          // Show the bar once the real button has scrolled above the viewport.
          const above = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.toggleAttribute('data-visible', above);
        },
        { threshold: 0 }
      ).observe(submit);
    });
  }

  function init(root = document) {
    initVideos(root);
    initReels(root);
    initRails(root);
    initStickyAtc(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
